/**
 * Connection Hook
 *
 * Hook for accessing WebSocket connection status and subscriptions.
 * Provides subscription health metrics including event counts,
 * reconnections, and stop events for diagnosing connection issues.
 */
import { useDataPlaneStore } from '../store';
import type { ConnectionError, ConnectionStatus, SubscriptionState } from '../store';

/**
 * Get the current connection status
 */
export function useConnectionStatus(): ConnectionStatus {
  return useDataPlaneStore((state) => state.connection.status);
}

/**
 * Get all connection information
 */
export function useConnection() {
  const status = useDataPlaneStore((state) => state.connection.status);
  const subscriptions = useDataPlaneStore((state) => state.connection.subscriptions);
  const recentErrors = useDataPlaneStore((state) => state.connection.recentErrors);
  const lastConnected = useDataPlaneStore((state) => state.connection.lastConnected);
  const lastDisconnected = useDataPlaneStore((state) => state.connection.lastDisconnected);

  return {
    status,
    subscriptions,
    recentErrors,
    lastConnected,
    lastDisconnected,
    isConnected: status === 'connected',
    isDisconnected: status === 'disconnected',
    isReconnecting: status === 'reconnecting',
  };
}

/**
 * Get recent connection errors
 */
export function useConnectionErrors(): ConnectionError[] {
  return useDataPlaneStore((state) => state.connection.recentErrors);
}

/**
 * Get subscription state for a specific entity type
 */
export function useSubscription(entityType: string): SubscriptionState | undefined {
  return useDataPlaneStore((state) => state.connection.subscriptions.get(entityType));
}

/**
 * Get all active subscriptions
 */
export function useActiveSubscriptions(): SubscriptionState[] {
  return useDataPlaneStore((state) => {
    const subscriptions: SubscriptionState[] = [];
    state.connection.subscriptions.forEach((sub) => {
      if (sub.status === 'active') {
        subscriptions.push(sub);
      }
    });
    return subscriptions;
  });
}

/**
 * Check if a specific entity type has an active subscription
 */
export function useHasActiveSubscription(entityType: string): boolean {
  return useDataPlaneStore((state) => {
    const sub = state.connection.subscriptions.get(entityType);
    return sub?.status === 'active';
  });
}

/**
 * Get subscription statistics including health metrics.
 *
 * Returns counts of active/error/stopped subscriptions,
 * plus aggregate reconnections, stops, and total events
 * across all tracked subscriptions.
 */
export function useSubscriptionStats() {
  return useDataPlaneStore((state) => {
    let total = 0;
    let active = 0;
    let error = 0;
    let stopped = 0;
    let totalEvents = 0;
    let totalReconnections = 0;
    let totalStops = 0;

    state.connection.subscriptions.forEach((sub) => {
      total++;
      if (sub.status === 'active') active++;
      if (sub.status === 'error') error++;
      if (sub.status === 'stopped') stopped++;
      totalEvents += sub.eventCount;
      totalReconnections += sub.reconnections;
      totalStops += sub.stoppedCount;
    });

    return { total, active, error, stopped, totalEvents, totalReconnections, totalStops };
  });
}

/**
 * Get a list of subscriptions that may be stale.
 * A subscription is considered stale if it claims to be 'active'
 * but hasn't received an event in over 2 minutes.
 */
export function useStaleSubscriptions(): SubscriptionState[] {
  return useDataPlaneStore((state) => {
    const STALE_THRESHOLD_MS = 2 * 60 * 1000;
    const now = Date.now();
    const stale: SubscriptionState[] = [];

    state.connection.subscriptions.forEach((sub) => {
      if (sub.status !== 'active') return;
      if (!sub.lastUpdate) return;
      if (now - sub.lastUpdate.getTime() > STALE_THRESHOLD_MS) {
        stale.push(sub);
      }
    });

    return stale;
  });
}
